/**
 * The alert-severity vocabulary — one scale for the whole product.
 *
 * Tiers are named for the action they ask of an operator, not for the size of the
 * statistic behind them, and they are the same three tiers whether an alert came
 * from the detection engine or from a reported leak episode. The list and its
 * sort weights are served by `/meta/config` from `boond_contracts/severity.py`,
 * so this module never decides what the tiers are — it only says how they look
 * and how they read.
 *
 * The literals below are a boot-time fallback for the window between first paint
 * and the config response (and for unit tests); they mirror the server defaults.
 *
 * There is deliberately no second scale. A component that wants "how bad is this"
 * imports from here — it must never define a local map, which is how the same
 * alert came to be styled two different ways on two pages.
 */
import { AlertCircle, AlertTriangle, Info, type LucideIcon } from "lucide-react";

import { chartColors } from "@/lib/tokens";

export type SeverityTier = "urgent" | "investigate" | "advisory";

/**
 * What a tier obliges: the defined operator response and the time budget for it.
 *
 * ISA-18.2 requires every priority to carry both — a tier that obliges nothing in
 * particular is a colour, not a priority. Served from `TIER_POLICY` in
 * `boond_contracts/severity.py`, so a tooltip, a legend and an SLA badge all
 * quote one sentence rather than three paraphrases written months apart.
 */
export interface TierPolicy {
  response: string;
  max_response_hours: number;
  /** Design share of the live worklist, per EEMUA 191's 5/15/80 guidance. */
  target_share: number;
}

const FALLBACK_TIERS: SeverityTier[] = ["urgent", "investigate", "advisory"];
const FALLBACK_RANK: Record<string, number> = {
  urgent: 3,
  investigate: 2,
  advisory: 1,
};
const FALLBACK_POLICY: Record<string, TierPolicy> = {
  urgent: {
    response: "Dispatch a crew today — water is being lost now.",
    max_response_hours: 24,
    target_share: 0.05,
  },
  investigate: {
    response: "Schedule an inspection this week.",
    max_response_hours: 168,
    target_share: 0.15,
  },
  advisory: {
    response: "Record and review in the next planning cycle.",
    max_response_hours: 720,
    target_share: 0.8,
  },
};

let tiers: SeverityTier[] = FALLBACK_TIERS;
let ranks: Record<string, number> = FALLBACK_RANK;
let policies: Record<string, TierPolicy> = FALLBACK_POLICY;

/** Called once by the config bootstrap, alongside `hydrateThresholds`. */
export function hydrateSeverity(
  nextTiers: string[] | undefined,
  nextRank: Record<string, number> | undefined,
  nextPolicy?: Record<string, TierPolicy>,
): void {
  if (nextTiers?.length) tiers = nextTiers as SeverityTier[];
  if (nextRank && Object.keys(nextRank).length) ranks = nextRank;
  if (nextPolicy && Object.keys(nextPolicy).length) policies = nextPolicy;
}

/** What this tier asks an operator to do, and how soon. */
export function severityPolicy(tier: string): TierPolicy | undefined {
  return policies[tier];
}

/** The tiers, worst first — the order filters, legends and sorts iterate in. */
export function severityTiers(): SeverityTier[] {
  return tiers;
}

/** Sort weight; unknown tiers sort below every known one. */
/**
 * A payload's count for `tier` (e.g. `AlertSummary.urgent`), read by tier name so
 * callers iterate `severityTiers()` instead of spelling the tiers out.
 */
export function tierCount(counts: object, tier: string): number {
  const value = (counts as Record<string, unknown>)[tier];
  return typeof value === "number" ? value : 0;
}

export function severityRank(tier: string): number {
  return ranks[tier] ?? -1;
}

/** Tier → its status-colour token. One place decides which hue a tier owns. */
function tokenFor(tier: string) {
  if (tier === "urgent") return chartColors.critical;
  if (tier === "investigate") return chartColors.warn;
  return chartColors.supplyWindow;
}

/**
 * Tier → colour. Reads the same three status tokens the rest of the app uses, so
 * an urgent alert is the same red as a critical sensor.
 */
export function severityColor(tier: string): string {
  return tokenFor(tier)();
}

/**
 * Tier → a faint wash of its colour, for a badge's background.
 *
 * Use this instead of appending an alpha suffix to `severityColor()`. The tokens
 * resolve to `rgb(r g b)`, so `` `${severityColor(t)}22` `` yields `rgb(r g b)22`
 * — invalid CSS, which the DOM drops silently. Every severity badge in the app
 * was doing exactly that, so none of them had a background at all.
 */
export function severityTint(tier: string, alpha = 0.14): string {
  return tokenFor(tier)(alpha);
}

/**
 * Tier → the icon that leads its row.
 *
 * Lives here for the same reason the colour does: a local `tier === "urgent" ?
 * ... : ...` in a feature is a second definition of the vocabulary, so a fourth
 * tier or a rename would land in one place and not the other. Shape-coded as
 * well as colour-coded because colour alone is not an accessible carrier of
 * severity.
 */
export function severityIcon(tier: string): LucideIcon {
  if (tier === "urgent") return AlertTriangle;
  if (tier === "investigate") return AlertCircle;
  return Info;
}

/** i18n key for a tier's label — one key namespace, used by every badge. */
export function severityLabelKey(tier: string): string {
  return `severity.${tier}`;
}
